import { RefObject, useEffect } from "react";

import { createRafThrottle } from "./rafThrottle";

// For a page-scrolled list whose shared Table no longer scrolls its rows internally (paired with
// useSectionScrollBottom above): the shared Table's Head has no cells-only wrapper (cells and the unused
// `.export` slot are direct children of `.table-header`, Table's Head.tsx), so the column header itself is
// made an `overflow-x: hidden` box (via the `pageScrolledTable()` SCSS mixin) and synced to the rows' own
// `scrollLeft` -- simpler than transform-shifting a wrapper that doesn't exist here, and no change to the
// shared Head component.
//
// A single listener on the container ROOT, added in the CAPTURE phase, avoids depending on when the Table
// mounts -- `.table-header`/`.table-content` might not exist yet when the effect runs (the first render's
// empty state, or a 0 -> N result count within the same mode, neither of which changes a "which Table
// variant" key). `scroll` doesn't bubble, but capture-phase listeners still fire on every ancestor as the
// event travels DOWN to its target, so this one listener sees every `.table-content` scroll under
// `rootRef` for as long as the hook is mounted, regardless of when that `.table-content` itself came into
// existence. `event.target` (the element actually scrolled) is looked up fresh on EVERY event, not cached.
const TABLE_CONTENT_SELECTOR = ".table-content";
const TABLE_WRAPPER_SELECTOR = ".table-wrapper";
const TABLE_HEADER_SELECTOR = ".table-header";
/** Set on a `.table-wrapper` while its sticky header is pinned; the `pageScrolledTable()` SCSS mixin
 *  styles the pinned header from it. An attribute, not a class: React owns `className` and would drop an
 *  added class on the Table's next render (e.g. its loading state changing). */
export const HEADER_PINNED_ATTRIBUTE = "data-header-pinned";

// A header is pinned once its wrapper's top has scrolled above the header's sticky `top` offset -- from
// then on the header is held in place instead of sitting at the top of the wrapper. The offset is read
// from the computed style, so the SCSS stays the only place it's set.
const markPinnedHeaders = (root: HTMLElement, scroller: Element): void => {
  const scrollerTop = scroller.getBoundingClientRect().top;
  root.querySelectorAll(TABLE_WRAPPER_SELECTOR).forEach((wrapper) => {
    const header = wrapper.querySelector(TABLE_HEADER_SELECTOR);
    if (!header) return;
    const stickyTop = parseFloat(getComputedStyle(header).top) || 0;
    wrapper.toggleAttribute(HEADER_PINNED_ATTRIBUTE, wrapper.getBoundingClientRect().top < scrollerTop + stickyTop);
  });
};

export interface UseStickyTableHeaderSyncOptions {
  /** Ref to the feature's own root element -- the one listener below is attached here, in the capture
   *  phase, once for the hook's whole lifetime. Reuse the same root ref `useSectionScrollBottom` already
   *  returns rather than adding a second one to the same DOM node. */
  rootRef: RefObject<HTMLElement>;
  /** CSS selector for the scrollable ancestor whose scroll marks pinned headers, found via `closest()`
   *  from `rootRef` -- the same selector passed to `useSectionScrollBottom`. */
  scrollContainerSelector: string;
}

/**
 * Keeps a `position: sticky` table header horizontally in step with its rows: `.table-header` is an
 * `overflow-x: hidden` box (via the `pageScrolledTable()` SCSS mixin), so it needs its `scrollLeft` pushed
 * explicitly -- it never receives scroll input of its own. Also marks each `.table-wrapper` whose header
 * is currently pinned (`HEADER_PINNED_ATTRIBUTE`) as the section (`scrollContainerSelector`) scrolls.
 * Pattern for the rAF throttle: `src/containers/Chat/hooks/useScrollToBottom.ts`.
 */
export const useStickyTableHeaderSync = ({
  rootRef,
  scrollContainerSelector,
}: UseStickyTableHeaderSyncOptions): void => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const { schedule, cancel } = createRafThrottle();
    const handleScroll = (event: Event) => {
      const content = event.target;
      if (!(content instanceof Element) || !content.matches(TABLE_CONTENT_SELECTOR)) return;
      const header = content.closest(TABLE_WRAPPER_SELECTOR)?.querySelector<HTMLElement>(TABLE_HEADER_SELECTOR);
      if (!header) return;

      schedule(() => {
        header.scrollLeft = content.scrollLeft;
      });
    };

    // `capture: true` is load-bearing: `scroll` events don't bubble, so a listener on `root` in the
    // (default) bubble phase would never see one dispatched on a descendant `.table-content`.
    root.addEventListener("scroll", handleScroll, { capture: true, passive: true });

    // The section scroller is an ANCESTOR of `root` (or `root` itself, via `closest()` matching its own
    // element), so its own scroll events never reach the capture listener above -- they need a listener
    // of their own. Its own throttle, so a pending horizontal sync and a pending pinned check never drop
    // each other.
    const scroller = root.closest(scrollContainerSelector);
    const pinnedCheck = createRafThrottle();
    const handleSectionScroll = () => {
      if (scroller) pinnedCheck.schedule(() => markPinnedHeaders(root, scroller));
    };
    scroller?.addEventListener("scroll", handleSectionScroll, { passive: true });

    return () => {
      root.removeEventListener("scroll", handleScroll, { capture: true });
      scroller?.removeEventListener("scroll", handleSectionScroll);
      cancel();
      pinnedCheck.cancel();
    };
  }, [rootRef, scrollContainerSelector]);
};

export default useStickyTableHeaderSync;
